/**
 * Comforty Theme JavaScript
 * Clean and scalable Shopify theme functionality
 */

(function () {
  'use strict';

  // Theme object
  window.Comforty = window.Comforty || {};

  /**
   * Cart functionality
   */
  Comforty.Cart = {
    init() {
      this.cartDrawer = document.querySelector('[data-cart-drawer]');
      this.cartCount = document.querySelectorAll('[data-cart-count]');
      this.bindEvents();
    },

    bindEvents() {
      // Quick add buttons
      document.querySelectorAll('[data-quick-add]').forEach(btn => {
        btn.addEventListener('click', this.handleQuickAdd.bind(this));
      });

      // Cart toggle
      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        btn.addEventListener('click', this.toggleCart.bind(this));
      });

      // Close cart drawer
      document.querySelectorAll('[data-cart-close]').forEach(btn => {
        btn.addEventListener('click', this.closeCart.bind(this));
      });

      // Cart overlay click
      const overlay = document.querySelector('[data-cart-overlay]');
      if (overlay) {
        overlay.addEventListener('click', this.closeCart.bind(this));
      }
    },

    async handleQuickAdd(e) {
      e.preventDefault();
      const btn = e.currentTarget;
      const variantId = btn.dataset.variantId;

      if (!variantId) return;

      btn.classList.add('is-loading');

      try {
        await this.addItem(variantId);

        const cart = await this.fetchCart();
        this.updateCartCount(cart?.item_count || 0);
        this.showToast(this.getStrings().itemAdded || 'Produit ajoute au panier', 'success');
        this.openCart();

      } catch (error) {
        console.error('Cart error:', error);
        this.showToast(this.getStrings().error || 'Une erreur est survenue', 'error');
      } finally {
        btn.classList.remove('is-loading');
      }
    },

    getRoutes() {
      return window.theme?.routes || window.routes || {};
    },

    getStrings() {
      return window.theme?.strings || window.cartStrings || {};
    },

    async fetchCart() {
      const routes = this.getRoutes();
      const cartUrl = routes.cart_url && routes.cart_url.endsWith('.js')
        ? routes.cart_url
        : `${routes.cart_url || '/cart'}.js`;
      const response = await fetch(cartUrl, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) {
        throw new Error('Unable to fetch cart');
      }
      return response.json();
    },

    async addItem(variantId) {
      const routes = this.getRoutes();
      const baseAddUrl = routes.cart_add_url || '/cart/add';
      const addJsUrl = baseAddUrl.endsWith('.js') ? baseAddUrl : `${baseAddUrl}.js`;

      try {
        const ajaxResponse = await fetch(addJsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });
        const ajaxData = await ajaxResponse.json();
        if (!ajaxResponse.ok || ajaxData?.status || ajaxData?.errors) {
          throw new Error(ajaxData?.description || ajaxData?.message || ajaxData?.errors || 'Add to cart failed');
        }
        return ajaxData;
      } catch (error) {
        // Some storefront/proxy setups intermittently fail on /cart/add.js.
        const fallbackResponse = await fetch(baseAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            Accept: 'text/html,application/xhtml+xml',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new URLSearchParams({
            id: String(variantId),
            quantity: '1'
          }).toString()
        });

        if (!fallbackResponse.ok) {
          throw error;
        }
      }
    },

    updateCartCount(count) {
      this.cartCount.forEach(el => {
        el.textContent = count;
        if (count > 0) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
        el.classList.add('is-updated');
        setTimeout(() => el.classList.remove('is-updated'), 300);
      });
    },

    toggleCart(e) {
      e.preventDefault();
      if (this.cartDrawer?.classList.contains('is-open')) {
        this.closeCart();
      } else {
        this.openCart();
      }
    },

    openCart() {
      if (this.cartDrawer) {
        this.cartDrawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        return;
      }

      const routes = this.getRoutes();
      window.location.href = routes.cart_url || '/cart';
    },

    closeCart() {
      this.cartDrawer?.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    showToast(message, type = 'success') {
      const toast = document.createElement('div');
      toast.className = `toast toast--${type}`;
      toast.textContent = message;
      document.body.appendChild(toast);

      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });

      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    }
  };

  /**
   * Wishlist functionality
   */
  Comforty.Wishlist = {
    storageKey: 'comforty_wishlist',

    init() {
      this.wishlist = this.getWishlist();
      this.updateUI();
      this.bindEvents();
    },

    getWishlist() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey)) || [];
      } catch {
        return [];
      }
    },

    saveWishlist() {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    },

    add(productId) {
      if (!this.wishlist.includes(productId)) {
        this.wishlist.push(productId);
        this.saveWishlist();
        this.updateUI();
      }
    },

    remove(productId) {
      this.wishlist = this.wishlist.filter(id => id !== productId);
      this.saveWishlist();
      this.updateUI();
    },

    toggle(productId) {
      if (this.wishlist.includes(productId)) {
        this.remove(productId);
      } else {
        this.add(productId);
      }
    },

    updateUI() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
        const productId = btn.dataset.productId;
        const isActive = this.wishlist.includes(productId);
        btn.classList.toggle('is-active', isActive);
      });
    },

    bindEvents() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const productId = btn.dataset.productId;
          this.toggle(productId);
        });
      });
    }
  };

  /**
   * Mobile menu functionality
   */
  Comforty.MobileMenu = {
    init() {
      this.menu = document.querySelector('[data-mobile-menu]');
      this.toggle = document.querySelector('[data-menu-toggle]');
      this.closeButton = this.menu?.querySelector('[data-menu-close]');
      this.overlay = this.menu?.querySelector('[data-menu-overlay]');
      this.panel = this.menu?.querySelector('.header__mobile-menu-panel');
      this.content = this.menu?.querySelector('[data-mobile-menu-content]');
      this.submenuPanels = Array.from(this.menu?.querySelectorAll('[data-mobile-submenu-panel]') || []);
      this.desktopMedia = window.matchMedia('(min-width: 990px)');

      if (!this.menu || !this.toggle) return;

      this.bindEvents();
    },

    bindEvents() {
      this.toggle.addEventListener('click', this.toggleMenu.bind(this));
      this.closeButton?.addEventListener('click', this.closeMenu.bind(this));
      this.overlay?.addEventListener('click', this.closeMenu.bind(this));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.closeMenu();
        }
      });

      this.menu.addEventListener('click', (e) => {
        const submenuOpenButton = e.target.closest('[data-mobile-submenu-open]');
        if (submenuOpenButton) {
          e.preventDefault();
          this.openSubmenu(submenuOpenButton.dataset.mobileSubmenuOpen);
          return;
        }

        const submenuBackButton = e.target.closest('[data-mobile-submenu-back]');
        if (submenuBackButton) {
          e.preventDefault();
          this.closeSubmenu();
          return;
        }

        const link = e.target.closest('a[href]');
        if (!link || link.closest('[data-menu-close]')) return;
        this.closeMenu();
      });

      this.desktopMedia.addEventListener('change', (event) => {
        if (event.matches) this.closeMenu();
      });
    },

    toggleMenu(e) {
      e?.preventDefault();
      if (this.isOpen()) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    },

    openMenu() {
      this.closeSubmenu();
      this.menu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.panel?.focus();
    },

    closeMenu() {
      this.closeSubmenu();
      this.menu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      this.toggle.setAttribute('aria-expanded', 'false');
    },

    openSubmenu(panelKey) {
      if (!panelKey || !this.submenuPanels.length) return;
      let hasMatch = false;
      this.submenuPanels.forEach((panel) => {
        const isTarget = panel.dataset.mobileSubmenuPanel === panelKey;
        panel.hidden = !isTarget;
        panel.classList.toggle('is-active', isTarget);
        if (isTarget) hasMatch = true;
      });

      if (hasMatch) {
        this.panel?.classList.add('is-submenu-open');
      }
    },

    closeSubmenu() {
      this.panel?.classList.remove('is-submenu-open');
      this.submenuPanels.forEach((panel) => {
        panel.hidden = true;
        panel.classList.remove('is-active');
      });
    },

    isOpen() {
      return this.menu.getAttribute('aria-hidden') === 'false';
    }
  };

  /**
   * Desktop mega menu (hover + keyboard support)
   */
  Comforty.DesktopMenu = {
    init() {
      this.menu = document.querySelector('[data-desktop-menu]');
      if (!this.menu) return;
      this.desktopMedia = window.matchMedia('(min-width: 990px)');
      this.header = this.menu.closest('.header');
      this.overlay = this.header?.querySelector('[data-mega-overlay]');
      this.closeTimers = new WeakMap();
      this.closeDelay = 180;

      this.bindEvents();
      this.bindMegaPanels();
      this.updateHeaderMegaState();
    },

    bindEvents() {
      this.menu.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-desktop-submenu-toggle]');
        if (!toggle || !this.menu.contains(toggle)) return;

        const item = toggle.closest('.header__menu-item--has-children');
        if (!item) return;

        // Desktop: keep link navigation, menu opens on hover/focus.
        if (!this.desktopMedia.matches) {
          e.preventDefault();
          const isOpen = item.classList.contains('is-open');
          this.closeSiblings(item);
          if (isOpen) {
            this.closeItem(item);
          } else {
            this.openItem(item, toggle);
          }
        }
      });

      this.menu.querySelectorAll('.header__menu-item--has-children').forEach((item) => {
        const toggle = item.querySelector('[data-desktop-submenu-toggle]');
        if (!toggle) return;

        item.addEventListener('mouseenter', () => {
          if (!this.desktopMedia.matches) return;
          this.clearCloseTimer(item);
          this.closeSiblings(item);
          this.openItem(item, toggle);
        });

        item.addEventListener('mouseleave', () => {
          if (!this.desktopMedia.matches) return;
          this.scheduleClose(item);
        });

        item.addEventListener('focusin', () => {
          this.clearCloseTimer(item);
          this.closeSiblings(item);
          this.openItem(item, toggle);
        });
      });

      document.addEventListener('click', (e) => {
        if (!this.menu.contains(e.target)) {
          this.closeAll();
        }
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          this.closeAll();
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAll();
        }
      });

      this.desktopMedia.addEventListener('change', () => {
        this.closeAll();
      });
    },

    bindMegaPanels() {
      this.menu.querySelectorAll('[data-mega-menu]').forEach((megaMenu) => {
        const triggers = Array.from(megaMenu.querySelectorAll('[data-mega-trigger]'));
        const panels = Array.from(megaMenu.querySelectorAll('[data-mega-panel]'));
        if (!triggers.length || !panels.length) return;

        const activatePanel = (panelKey) => {
          triggers.forEach((trigger) => {
            const isActive = trigger.dataset.megaTrigger === panelKey;
            if (isActive) {
              trigger.setAttribute('aria-current', 'true');
            } else {
              trigger.removeAttribute('aria-current');
            }
          });

          panels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.megaPanel === panelKey);
          });
        };

        triggers.forEach((trigger) => {
          trigger.addEventListener('mouseenter', () => {
            activatePanel(trigger.dataset.megaTrigger);
          });

          trigger.addEventListener('focus', () => {
            activatePanel(trigger.dataset.megaTrigger);
          });
        });
      });
    },

    openItem(item, toggle) {
      this.clearCloseTimer(item);
      item.classList.add('is-open');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'true');
      }
      this.updateHeaderMegaState();
    },

    closeItem(item) {
      this.clearCloseTimer(item);
      item.classList.remove('is-open');

      const toggle = item.querySelector('[data-desktop-submenu-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
      }
      this.updateHeaderMegaState();
    },

    closeSiblings(item) {
      const parentList = item.parentElement;
      if (!parentList) return;

      Array.from(parentList.children).forEach((sibling) => {
        if (sibling === item || !sibling.classList) return;

        this.clearCloseTimer(sibling);

        if (sibling.classList.contains('is-open')) {
          this.closeItem(sibling);
        }
      });
    },

    closeAll() {
      this.menu.querySelectorAll('.header__menu-item--has-children').forEach((item) => {
        this.clearCloseTimer(item);
      });

      this.menu.querySelectorAll('.header__menu-item--has-children.is-open').forEach((item) => {
        this.closeItem(item);
      });
      this.updateHeaderMegaState();
    },

    scheduleClose(item) {
      this.clearCloseTimer(item);

      const timer = window.setTimeout(() => {
        this.closeTimers.delete(item);
        this.closeItem(item);
      }, this.closeDelay);

      this.closeTimers.set(item, timer);
    },

    clearCloseTimer(item) {
      const timer = this.closeTimers.get(item);
      if (timer) {
        window.clearTimeout(timer);
        this.closeTimers.delete(item);
      }
    },

    updateHeaderMegaState() {
      if (!this.header) return;
      const hasOpenItem = this.menu.querySelector('.header__menu-item--has-children.is-open');
      this.header.classList.toggle('is-mega-open', Boolean(hasOpenItem) && this.desktopMedia.matches);
    }
  };

  /**
   * Search functionality
   */
  Comforty.Search = {
    init() {
      this.searchForms = document.querySelectorAll('[data-search-form]');
      this.abortController = null;
      this.bindEvents();
    },

    async fetchPredictiveResults(query) {
      const base = window.theme?.routes?.predictive_search_url || '/search/suggest';
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product,collection,page');
      params.set('resources[limit]', '6');
      params.set('resources[options][unavailable_products]', 'show');

      const candidates = [`${base}.json?${params.toString()}`, `${base}?${params.toString()}`];

      for (const url of candidates) {
        try {
          const response = await fetch(url, {
            signal: this.abortController.signal,
            headers: { Accept: 'application/json' }
          });
          const contentType = response.headers.get('content-type') || '';
          if (!response.ok || !contentType.includes('application/json')) continue;

          const data = await response.json();
          const results = data?.resources?.results || data?.resources || {};
          return {
            products: Array.isArray(results.products) ? results.products : [],
            collections: Array.isArray(results.collections) ? results.collections : [],
            pages: Array.isArray(results.pages) ? results.pages : []
          };
        } catch (error) {
          if (error.name === 'AbortError') throw error;
        }
      }

      return { products: [], collections: [], pages: [] };
    },

    bindEvents() {
      const openSearchButtons = document.querySelectorAll('[data-open-search]');

      this.searchForms.forEach((form) => {
        const input = form.querySelector('[data-search-input]');
        const results = form.querySelector('[data-search-results]');
        const header = form.closest('.header');
        const overlay = header ? header.querySelector('[data-search-overlay]') : null;
        const status = form.querySelector('[data-search-status]');
        const grid = form.querySelector('[data-search-grid]');
        const tabs = form.querySelector('[data-search-tabs]');
        const tabButtons = Array.from(form.querySelectorAll('[data-search-tab-button]'));
        const panels = Array.from(form.querySelectorAll('[data-search-panel]'));
        const productsList = form.querySelector('[data-search-products]');
        const collectionsList = form.querySelector('[data-search-collections]');
        const pagesList = form.querySelector('[data-search-pages]');
        const seeAll = form.querySelector('[data-search-all]');

        if (!input || !results || !status || !grid || !tabs || !tabButtons.length || !panels.length || !productsList || !collectionsList || !pagesList || !seeAll) return;

        const setActiveTab = (tabKey) => {
          tabButtons.forEach((button) => {
            button.classList.toggle('is-active', button.dataset.searchTabButton === tabKey);
          });
          panels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.searchPanel === tabKey);
          });
        };

        tabButtons.forEach((button) => {
          button.addEventListener('click', () => {
            setActiveTab(button.dataset.searchTabButton);
          });
        });

        let debounceTimer;
        const setSearchOpen = (isOpen) => {
          if (header) {
            header.classList.toggle('is-search-open', Boolean(isOpen));
          }
        };
        const hideResults = () => {
          results.hidden = true;
          if (overlay) overlay.hidden = true;
          setSearchOpen(false);
        };
        const showResults = () => {
          results.hidden = false;
          if (overlay) overlay.hidden = false;
          setSearchOpen(true);
        };
        const openSearch = () => {
          if (overlay) overlay.hidden = false;
          setSearchOpen(true);
          input.focus();
        };

        input.addEventListener('input', () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            this.performSearch({
              query: input.value,
              input,
              showResults,
              hideResults,
              results,
              status,
              grid,
              tabs,
              setActiveTab,
              productsList,
              collectionsList,
              pagesList,
              seeAll
            });
          }, 220);
        });

        input.addEventListener('focus', () => {
          if (input.value.trim().length >= 2) showResults();
        });

        input.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') hideResults();
        });

        document.addEventListener('click', (e) => {
          if (e.target.closest('[data-open-search]')) return;
          if (!form.contains(e.target)) {
            hideResults();
          }
        });

        if (overlay) {
          overlay.addEventListener('click', () => {
            hideResults();
          });
        }

        openSearchButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            openSearch();
          });
        });
      });
    },

    async performSearch(context) {
      const { query, showResults, hideResults, results, status, grid, tabs, setActiveTab, productsList, collectionsList, pagesList, seeAll } = context;

      if (!query.trim()) {
        status.textContent = 'Commencez a taper pour rechercher.';
        tabs.hidden = true;
        grid.hidden = true;
        seeAll.hidden = true;
        hideResults();
        return;
      }

      if (query.trim().length < 2) {
        status.textContent = 'Tapez au moins 2 caracteres.';
        tabs.hidden = true;
        grid.hidden = true;
        seeAll.hidden = true;
        showResults();
        return;
      }

      status.textContent = 'Recherche en cours...';
      tabs.hidden = true;
      grid.hidden = true;
      seeAll.hidden = true;
      showResults();

      try {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const { products, collections, pages } = await this.fetchPredictiveResults(query);

        productsList.innerHTML = this.renderProductResults(products);
        collectionsList.innerHTML = this.renderSimpleResults(collections, 'collection');
        pagesList.innerHTML = this.renderSimpleResults(pages, 'page');

        const total = products.length + collections.length + pages.length;
        if (total === 0) {
          status.textContent = 'Aucun resultat.';
          tabs.hidden = true;
          grid.hidden = true;
          seeAll.hidden = false;
        } else {
          status.textContent = '';
          tabs.hidden = false;
          grid.hidden = false;
          seeAll.hidden = false;

          if (products.length > 0) {
            setActiveTab('products');
          } else if (collections.length > 0) {
            setActiveTab('collections');
          } else {
            setActiveTab('pages');
          }
        }

        seeAll.href = `${window.theme.routes.search_url || '/search'}?q=${encodeURIComponent(query)}&type=product&options%5Bprefix%5D=last`;
        seeAll.textContent = `Voir tous les resultats pour "${query}"`;
        showResults();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Search error:', error);
        status.textContent = 'Erreur de recherche, veuillez reessayer.';
        tabs.hidden = true;
        grid.hidden = true;
        seeAll.hidden = true;
        showResults();
      }
    },

    renderProductResults(products) {
      return products
        .map((product) => {
          const image = product.image ? `<img src="${product.image}" alt="${product.title}" class="header__search-item-image">` : '';
          return `
            <a href="${product.url}" class="header__search-item">
              ${image}
              <span class="header__search-item-text">
                <span class="header__search-item-title">${product.title}</span>
              </span>
            </a>
          `;
        })
        .join('');
    },

    renderSimpleResults(items, type) {
      return items
        .map((item) => {
          const typeLabel = type === 'collection' ? 'Collection' : 'Page';
          return `
            <a href="${item.url}" class="header__search-item">
              <span class="header__search-item-text">
                <span class="header__search-item-title">${item.title}</span>
                <span class="header__search-item-meta">${typeLabel}</span>
              </span>
            </a>
          `;
        })
        .join('');
    }
  };

  /**
   * Lazy loading images
   */
  Comforty.LazyLoad = {
    init() {
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.classList.add('is-loaded');
              imageObserver.unobserve(img);
            }
          });
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
          imageObserver.observe(img);
        });
      }
    }
  };

  /**
   * Animation on scroll
   */
  Comforty.Animations = {
    init() {
      if ('IntersectionObserver' in window) {
        const animationObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-animated');
              animationObserver.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        });

        document.querySelectorAll('[data-animate]').forEach(el => {
          animationObserver.observe(el);
        });
      }
    }
  };

  /**
   * Product gallery thumbnails
   */
  Comforty.ProductGallery = {
    init() {
      document.querySelectorAll('.main-product').forEach((section) => {
        const mainImage = section.querySelector('.product__image');
        const thumbnails = Array.from(section.querySelectorAll('.product__thumbnail'));
        const prevButton = section.querySelector('[data-main-media-prev]');
        const nextButton = section.querySelector('[data-main-media-next]');

        if (!mainImage || thumbnails.length === 0) return;

        const setActiveThumbnail = (thumbnail) => {
          const nextSrc = thumbnail.dataset.imageSrc;
          const nextSrcset = thumbnail.dataset.imageSrcset;
          const nextAlt = thumbnail.dataset.imageAlt || '';
          const nextWidth = thumbnail.dataset.imageWidth;
          const nextHeight = thumbnail.dataset.imageHeight;

          if (!nextSrc) return;

          mainImage.src = nextSrc;
          if (nextSrcset) mainImage.srcset = nextSrcset;
          mainImage.alt = nextAlt;
          if (nextWidth) mainImage.width = Number(nextWidth);
          if (nextHeight) mainImage.height = Number(nextHeight);

          thumbnails.forEach((btn) => btn.classList.remove('is-active'));
          thumbnail.classList.add('is-active');
        };

        thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            setActiveThumbnail(thumbnail);
          });
        });

        const moveTo = (step) => {
          const activeIndex = thumbnails.findIndex((thumb) => thumb.classList.contains('is-active'));
          const currentIndex = activeIndex >= 0 ? activeIndex : 0;
          const nextIndex = (currentIndex + step + thumbnails.length) % thumbnails.length;
          setActiveThumbnail(thumbnails[nextIndex]);
        };

        if (prevButton) {
          prevButton.addEventListener('click', (event) => {
            event.stopPropagation();
            moveTo(-1);
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', (event) => {
            event.stopPropagation();
            moveTo(1);
          });
        }
      });
    }
  };

  /**
   * Product media lightbox
   */
  Comforty.ProductLightbox = {
    init() {
      document.querySelectorAll('.main-product').forEach((section) => {
        const media = section.querySelector('.product__media');
        const openTrigger = section.querySelector('[data-lightbox-open]');
        const lightbox = section.querySelector('[data-product-lightbox]');
        const closeButton = section.querySelector('[data-lightbox-close]');
        const prevButton = section.querySelector('[data-lightbox-prev]');
        const nextButton = section.querySelector('[data-lightbox-next]');
        const modalImage = section.querySelector('[data-lightbox-image]');
        const thumbs = Array.from(section.querySelectorAll('[data-lightbox-thumb]'));

        if (!media || !openTrigger || !lightbox || !modalImage || !thumbs.length) return;

        let currentIndex = 0;
        let zoomLevel = 1;
        const minZoom = 1;
        const maxZoom = 4;
        const zoomStep = 0.25;

        const applyZoom = () => {
          modalImage.style.transform = `scale(${zoomLevel})`;
          modalImage.style.cursor = zoomLevel > 1 ? 'move' : 'zoom-in';
        };

        const resetZoom = () => {
          zoomLevel = 1;
          modalImage.style.transformOrigin = 'center center';
          applyZoom();
        };

        const setZoomFromPoint = (nextZoom, clientX, clientY) => {
          const rect = modalImage.getBoundingClientRect();
          const xPercent = ((clientX - rect.left) / rect.width) * 100;
          const yPercent = ((clientY - rect.top) / rect.height) * 100;
          const safeX = Math.min(100, Math.max(0, xPercent));
          const safeY = Math.min(100, Math.max(0, yPercent));
          modalImage.style.transformOrigin = `${safeX}% ${safeY}%`;
          zoomLevel = Math.min(maxZoom, Math.max(minZoom, nextZoom));
          applyZoom();
        };

        const setActiveIndex = (index) => {
          currentIndex = (index + thumbs.length) % thumbs.length;

          const activeThumb = thumbs[currentIndex];
          if (!activeThumb) return;

          modalImage.src = activeThumb.dataset.imageSrc || '';
          modalImage.alt = activeThumb.dataset.imageAlt || '';
          resetZoom();

          thumbs.forEach((thumb, thumbIndex) => {
            const isActive = thumbIndex === currentIndex;
            thumb.classList.toggle('is-active', isActive);
            if (isActive) thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          });

          if (prevButton) prevButton.disabled = thumbs.length <= 1;
          if (nextButton) nextButton.disabled = thumbs.length <= 1;
        };

        const openAtCurrentProductImage = () => {
          const activeMainThumb = section.querySelector('.product__thumbnail.is-active');
          const initialIndex = activeMainThumb ? Number(activeMainThumb.dataset.imageIndex || 0) : 0;
          setActiveIndex(initialIndex);
          lightbox.hidden = false;
          document.body.classList.add('overflow-hidden');
        };

        const close = () => {
          lightbox.hidden = true;
          document.body.classList.remove('overflow-hidden');
          resetZoom();
        };

        openTrigger.addEventListener('click', (event) => {
          event.preventDefault();
          openAtCurrentProductImage();
        });

        media.addEventListener('click', (event) => {
          if (event.target.closest('.product__zoom-trigger')) return;
          openAtCurrentProductImage();
        });

        if (closeButton) {
          closeButton.addEventListener('click', close);
        }

        lightbox.addEventListener('click', (event) => {
          if (event.target === lightbox) close();
        });

        if (prevButton) {
          prevButton.addEventListener('click', () => {
            setActiveIndex(currentIndex - 1);
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            setActiveIndex(currentIndex + 1);
          });
        }

        thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            const nextIndex = Number(thumb.dataset.lightboxIndex || 0);
            setActiveIndex(nextIndex);
          });
        });

        modalImage.addEventListener('click', (event) => {
          event.preventDefault();
          if (zoomLevel > 1) {
            resetZoom();
            return;
          }
          setZoomFromPoint(2, event.clientX, event.clientY);
        });

        modalImage.addEventListener('mousemove', (event) => {
          if (zoomLevel <= 1) return;
          setZoomFromPoint(zoomLevel, event.clientX, event.clientY);
        });

        modalImage.addEventListener('wheel', (event) => {
          event.preventDefault();
          const direction = event.deltaY < 0 ? 1 : -1;
          const nextZoom = zoomLevel + (direction * zoomStep);
          setZoomFromPoint(nextZoom, event.clientX, event.clientY);
        }, { passive: false });

        document.addEventListener('keydown', (event) => {
          if (lightbox.hidden) return;

          if (event.key === 'Escape') {
            close();
          }

          if (event.key === 'ArrowLeft') {
            setActiveIndex(currentIndex - 1);
          }

          if (event.key === 'ArrowRight') {
            setActiveIndex(currentIndex + 1);
          }
        });
      });
    }
  };

  /**
   * Product information tabs
   */
  Comforty.ProductTabs = {
    init() {
      document.querySelectorAll('[data-product-tabs]').forEach((tabs) => {
        const buttons = tabs.querySelectorAll('[data-tab-button]');
        const panels = tabs.querySelectorAll('[data-tab-panel]');

        if (!buttons.length || !panels.length) return;

        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            const target = button.dataset.tabTarget;

            buttons.forEach((btn) => {
              const isActive = btn === button;
              btn.classList.toggle('is-active', isActive);
              btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });

            panels.forEach((panel) => {
              const isActive = panel.dataset.tabPanel === target;
              panel.classList.toggle('is-active', isActive);
              panel.hidden = !isActive;
            });
          });
        });
      });
    }
  };

  /**
   * Product variant selector (price + availability sync)
   */
  Comforty.ProductForm = {
    init() {
      document.querySelectorAll('.main-product').forEach((section) => {
        const form = section.querySelector('[data-product-form]');
        const variantInput = section.querySelector('[data-variant-id-input]');
        const variantsScript = section.querySelector('[data-product-variants]');
        const optionInputs = Array.from(section.querySelectorAll('.product__option-value input[type="radio"]'));
        const enableSwatchImagePreview = section.dataset.enableSwatchImagePreview !== 'false';

        if (!form || !variantInput || !variantsScript) return;

        let variants = [];
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (error) {
          console.error('Failed to parse product variants JSON:', error);
          return;
        }

        const initialVariantId = Number(variantInput.value);
        const initialVariant = this.findVariantById(variants, initialVariantId);
        const fallbackVariant = initialVariant || variants.find((variant) => variant.available) || variants[0] || null;

        if (optionInputs.length) {
          this.ensureSelectedOptions(section, fallbackVariant);
          optionInputs.forEach((input) => {
            input.addEventListener('change', () => {
              const isColorOption = input.closest('.product__option')?.classList.contains('product__option--color');
              if (isColorOption && enableSwatchImagePreview) {
                this.navigateToVariant(section, variants, fallbackVariant);
              } else {
                this.syncVariantFromInputs(section, variants, variantInput, fallbackVariant);
              }
            });
          });
          this.syncVariantFromInputs(section, variants, variantInput, fallbackVariant);
        } else {
          this.updateVariantUI(section, fallbackVariant, variantInput);
        }
      });
    },

    findVariantById(variants, variantId) {
      return variants.find((variant) => Number(variant.id) === Number(variantId)) || null;
    },

    navigateToVariant(section, variants, fallbackVariant) {
      const selectedOptions = this.getSelectedOptions(section);
      const selectedVariant = this.findVariantByOptions(variants, selectedOptions) || fallbackVariant;
      if (!selectedVariant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(selectedVariant.id));
      window.location.href = url.pathname + url.search;
    },

    normalizeOptionValue(value) {
      return String(value == null ? '' : value)
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    },

    ensureSelectedOptions(section, fallbackVariant) {
      if (!fallbackVariant) return;

      section.querySelectorAll('.product__option').forEach((group) => {
        const checkedInput = group.querySelector('.product__option-value input[type="radio"]:checked');
        if (checkedInput) return;

        const optionPosition = Number(group.dataset.optionPosition || 0);
        const fallbackValue = fallbackVariant[`option${optionPosition}`];
        if (!fallbackValue) return;

        const input = Array.from(group.querySelectorAll('.product__option-value input[type="radio"]')).find((field) => {
          return this.normalizeOptionValue(field.value) === this.normalizeOptionValue(fallbackValue);
        });

        if (input) input.checked = true;
      });

      this.updateSelectedOptionValues(section);
    },

    syncVariantFromInputs(section, variants, variantInput, fallbackVariant) {
      this.updateSelectedOptionValues(section);
      const selectedOptions = this.getSelectedOptions(section);
      let selectedVariant = this.findVariantByOptions(variants, selectedOptions);

      if (!selectedVariant && fallbackVariant) {
        this.setSelectedOptionsFromVariant(section, fallbackVariant);
        selectedVariant = fallbackVariant;
      }

      this.updateVariantUI(section, selectedVariant, variantInput);
    },

    updateSelectedOptionValues(section) {
      section.querySelectorAll('.product__option').forEach((optionGroup) => {
        const selectedValueTarget = optionGroup.querySelector('[data-selected-option-value]');
        const checkedInput = optionGroup.querySelector('.product__option-value input[type="radio"]:checked');

        if (selectedValueTarget && checkedInput) {
          selectedValueTarget.textContent = checkedInput.value;
        }
      });
    },

    getSelectedOptions(section) {
      const optionGroups = section.querySelectorAll('.product__option');
      if (!optionGroups.length) return [];

      const selectedByPosition = new Array(optionGroups.length).fill(null);
      optionGroups.forEach((group) => {
        const optionPosition = Number(group.dataset.optionPosition || 0);
        const checkedInput = group.querySelector('.product__option-value input[type="radio"]:checked');
        if (optionPosition > 0 && checkedInput) {
          selectedByPosition[optionPosition - 1] = checkedInput.value;
        }
      });

      if (selectedByPosition.some((value) => value === null)) return [];
      return selectedByPosition;
    },

    findVariantByOptions(variants, selectedOptions) {
      if (!selectedOptions.length) return null;
      return variants.find((variant) => {
        return selectedOptions.every((optionValue, index) => {
          if (!optionValue) return true;
          return this.normalizeOptionValue(variant[`option${index + 1}`]) === this.normalizeOptionValue(optionValue);
        });
      });
    },

    setSelectedOptionsFromVariant(section, variant) {
      if (!variant || !Array.isArray(variant.options)) return;

      section.querySelectorAll('.product__option').forEach((group) => {
        const optionPosition = Number(group.dataset.optionPosition || 0);
        const optionValue = variant.options[optionPosition - 1];
        if (!optionPosition || optionValue === undefined) return;

        const input = Array.from(group.querySelectorAll('.product__option-value input[type="radio"]')).find((field) => {
          return this.normalizeOptionValue(field.value) === this.normalizeOptionValue(optionValue);
        });
        if (input) input.checked = true;
      });

      this.updateSelectedOptionValues(section);
    },

    updateVariantUI(section, variant, variantInput) {
      const addToCartButton = section.querySelector('.product__add-to-cart');
      const addToCartText = section.querySelector('[data-add-to-cart-text]');
      const enableSwatchImagePreview = section.dataset.enableSwatchImagePreview !== 'false';

      if (!variant) {
        if (addToCartButton) addToCartButton.disabled = true;
        if (addToCartText) addToCartText.textContent = window.theme?.strings?.unavailable || 'Unavailable';
        this.updateHTPrice(section, null);
        return;
      }

      variantInput.value = variant.id;
      this.updateUrl(variant);
      this.updatePrice(section, variant);
      this.updateHTPrice(section, variant);
      if (enableSwatchImagePreview) {
        this.updateMedia(section, variant);
      }

      if (!addToCartButton || !addToCartText) return;

      if (variant.available) {
        addToCartButton.disabled = false;
        addToCartText.textContent = window.theme?.strings?.addToCart || 'Add to cart';
      } else {
        addToCartButton.disabled = true;
        addToCartText.textContent = window.theme?.strings?.soldOut || 'Sold out';
      }
    },

    updateMedia(section, variant) {
      const mainImage = section.querySelector('.product__image');
      if (!mainImage || !variant?.featured_media) return;

      const thumbnails = Array.from(section.querySelectorAll('.product__thumbnail'));
      const matchingThumbnail = thumbnails.find((thumbnail) => {
        return String(thumbnail.dataset.imageId || '') === String(variant.featured_media.id);
      });

      if (matchingThumbnail) {
        mainImage.src = matchingThumbnail.dataset.imageSrc || mainImage.src;
        if (matchingThumbnail.dataset.imageSrcset) mainImage.srcset = matchingThumbnail.dataset.imageSrcset;
        mainImage.alt = matchingThumbnail.dataset.imageAlt || '';
        if (matchingThumbnail.dataset.imageWidth) mainImage.width = Number(matchingThumbnail.dataset.imageWidth);
        if (matchingThumbnail.dataset.imageHeight) mainImage.height = Number(matchingThumbnail.dataset.imageHeight);

        thumbnails.forEach((thumbnail) => thumbnail.classList.remove('is-active'));
        matchingThumbnail.classList.add('is-active');
        return;
      }

      mainImage.src = variant.featured_media.src || mainImage.src;
      if (variant.featured_media.srcset) mainImage.srcset = variant.featured_media.srcset;
      mainImage.alt = variant.featured_media.alt || '';
      if (variant.featured_media.width) mainImage.width = Number(variant.featured_media.width);
      if (variant.featured_media.height) mainImage.height = Number(variant.featured_media.height);
    },

    updateUrl(variant) {
      if (!variant?.id || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    },

    updatePrice(section, variant) {
      const salePrice = section.querySelector('[data-price-sale]');
      const comparePrice = section.querySelector('[data-price-compare]');
      const regularPrice = section.querySelector('[data-price-regular]');
      const saleBadge = section.querySelector('[data-price-badge]');
      const hasComparePrice = Number(variant.compare_at_price || 0) > Number(variant.price || 0);

      if (hasComparePrice) {
        if (salePrice) {
          salePrice.textContent = variant.price_formatted;
          salePrice.hidden = false;
        }

        if (comparePrice) {
          comparePrice.textContent = variant.compare_at_price_formatted;
          comparePrice.hidden = false;
        }

        if (saleBadge) {
          saleBadge.hidden = false;
        }

        if (regularPrice) {
          regularPrice.hidden = true;
        }
      } else {
        if (regularPrice) {
          regularPrice.textContent = variant.price_formatted;
          regularPrice.hidden = false;
        }

        if (salePrice) {
          salePrice.hidden = true;
        }

        if (comparePrice) {
          comparePrice.hidden = true;
        }

        if (saleBadge) {
          saleBadge.hidden = true;
        }
      }
    },

    updateHTPrice(section, variant) {
      const htPrice = section.querySelector('[data-price-ht]');
      if (!htPrice) return;

      if (!variant || Number(variant.price) <= 0) {
        htPrice.textContent = '-- HT';
        return;
      }

      const vatRate = Number(section.dataset.vatRate || 20);
      const normalizedVatRate = Number.isFinite(vatRate) && vatRate >= 0 ? vatRate : 20;
      const multiplier = 1 + normalizedVatRate / 100;
      const priceHtCents = Math.round(Number(variant.price) / multiplier);
      const priceHt = priceHtCents / 100;
      const formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(priceHt);

      htPrice.textContent = `${formatted}€ HT`;
    }
  };

  /**
   * Product quantity controls (+ / -)
   */
  Comforty.ProductQuantity = {
    init() {
      document.querySelectorAll('.main-product').forEach((section) => {
        const input = section.querySelector('.product__quantity-input');
        const minusBtn = section.querySelector('[data-quantity-minus]');
        const plusBtn = section.querySelector('[data-quantity-plus]');
        if (!input || !minusBtn || !plusBtn) return;

        const getMin = () => Number(input.min || 1);
        const getMax = () => {
          const max = Number(input.max);
          return Number.isFinite(max) && max > 0 ? max : null;
        };
        const normalize = (value) => {
          const min = getMin();
          const max = getMax();
          let next = Number.isFinite(value) ? value : min;
          if (next < min) next = min;
          if (max !== null && next > max) next = max;
          return next;
        };
        const setInputValue = (nextValue) => {
          input.value = String(normalize(nextValue));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        };

        minusBtn.addEventListener('click', () => setInputValue(Number(input.value || getMin()) - 1));
        plusBtn.addEventListener('click', () => setInputValue(Number(input.value || getMin()) + 1));
        input.addEventListener('change', () => setInputValue(Number(input.value)));
      });
    }
  };

  /**
   * Product short description toggle
   */
  Comforty.ProductDescription = {
    init() {
      document.querySelectorAll('.main-product [data-product-description-wrap]').forEach((wrap) => {
        const toggle = wrap.querySelector('[data-product-description-toggle]');
        if (!toggle) return;

        toggle.addEventListener('click', () => {
          const isExpanded = wrap.classList.toggle('is-expanded');
          wrap.classList.toggle('is-collapsed', !isExpanded);
          toggle.setAttribute('aria-expanded', String(isExpanded));
          toggle.textContent = isExpanded ? 'Voir moins' : 'Voir plus';
        });
      });
    }
  };

  /**
   * Collection filters (auto-apply)
   */
  Comforty.CollectionFilters = {
    init() {
      document.querySelectorAll('.main-collection .collection__form, .main-search .collection__form').forEach((form) => {
        const section = form.closest('.main-collection');
        const openButton = form.querySelector('[data-mobile-filters-open]');
        const closeButton = form.querySelector('[data-mobile-filters-close]');
        const overlay = form.querySelector('[data-mobile-filters-overlay]');
        const applyButton = form.querySelector('[data-mobile-filters-apply]');
        const mobileCountTarget = form.querySelector('[data-mobile-results-count]');
        const desktopCountTarget = form.querySelector('[data-results-count]');
        const desktopSortSelect = form.querySelector('[data-desktop-sort-select]');
        const mobileSortRadios = Array.from(form.querySelectorAll('[data-mobile-sort-radio]'));
        const currentCount = Number(form.dataset.initialResultsCount || 0);
        const mediaQuery = window.matchMedia('(max-width: 749px)');
        let previewDebounceTimer;
        let previewController;

        const syncCount = (count) => {
          if (mobileCountTarget) mobileCountTarget.textContent = String(count);
          if (desktopCountTarget) desktopCountTarget.textContent = String(count);
        };

        const openDrawer = () => {
          if (!section || !mediaQuery.matches) return;
          section.classList.add('is-mobile-drawer-open');
          document.body.classList.add('overflow-hidden');
        };

        const closeDrawer = () => {
          if (!section) return;
          section.classList.remove('is-mobile-drawer-open');
          document.body.classList.remove('overflow-hidden');
        };

        const fetchPreviewCount = () => {
          if (!mediaQuery.matches || !applyButton) return;

          if (previewController) previewController.abort();
          previewController = new AbortController();
          applyButton.classList.add('is-loading');

          const params = new URLSearchParams(new FormData(form));
          params.delete('page');

          fetch(`${form.action}?${params.toString()}`, { signal: previewController.signal })
            .then((response) => response.text())
            .then((html) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const countNode = doc.querySelector('[data-results-count-text] [data-results-count]');
              if (countNode) {
                const countText = (countNode.textContent || '').trim();
                const parsedCount = Number(countText.replace(/[^\d]/g, ''));
                if (!Number.isNaN(parsedCount)) syncCount(parsedCount);
              }
            })
            .catch((error) => {
              if (error.name !== 'AbortError') {
                console.error('Collection preview count failed:', error);
              }
            })
            .finally(() => {
              applyButton.classList.remove('is-loading');
            });
        };

        const handleDesktopChange = () => {
          if (mediaQuery.matches) return;
          form.submit();
        };

        const handleMobileChange = () => {
          if (!mediaQuery.matches) return;
          clearTimeout(previewDebounceTimer);
          previewDebounceTimer = setTimeout(fetchPreviewCount, 280);
        };

        const syncSortValueAcrossViews = (selectedValue) => {
          if (desktopSortSelect && desktopSortSelect.value !== selectedValue) {
            desktopSortSelect.value = selectedValue;
          }

          mobileSortRadios.forEach((radio) => {
            radio.checked = radio.value === selectedValue;
          });
        };

        const syncSortInputsState = () => {
          const isMobile = mediaQuery.matches;
          if (desktopSortSelect) {
            desktopSortSelect.disabled = isMobile;
          }
          mobileSortRadios.forEach((radio) => {
            radio.disabled = !isMobile;
          });
        };

        if (desktopSortSelect) {
          desktopSortSelect.addEventListener('change', () => {
            syncSortValueAcrossViews(desktopSortSelect.value);
          });
        }

        mobileSortRadios.forEach((radio) => {
          radio.addEventListener('change', () => {
            if (radio.checked) {
              syncSortValueAcrossViews(radio.value);
            }
          });
        });

        form.querySelectorAll('input[type="checkbox"], input[type="radio"], select').forEach((field) => {
          field.addEventListener('change', () => {
            handleDesktopChange();
            handleMobileChange();
          });
        });

        const applyPriceRange = () => {
          if (mediaQuery.matches) {
            handleMobileChange();
          } else {
            form.submit();
          }
        };

        form.querySelectorAll('.collection__price-range').forEach((range) => {
          const minInput = range.querySelector('[data-price-min-input]');
          const maxInput = range.querySelector('[data-price-max-input]');
          const minRange = range.querySelector('[data-price-min-range]');
          const maxRange = range.querySelector('[data-price-max-range]');

          if (minInput && maxInput && minRange && maxRange) {
            const absoluteMin = Number(minRange.min || 0);
            const absoluteMax = Number(maxRange.max || 0);

            const clampValue = (value, fallback) => {
              const parsed = Number(value);
              if (Number.isNaN(parsed)) return fallback;
              return Math.min(Math.max(parsed, absoluteMin), absoluteMax);
            };

            const updateSliderTrack = (minValue, maxValue) => {
              const safeMax = absoluteMax || 1;
              const minPercent = ((minValue - absoluteMin) / (safeMax - absoluteMin || 1)) * 100;
              const maxPercent = ((maxValue - absoluteMin) / (safeMax - absoluteMin || 1)) * 100;
              range.style.setProperty('--price-min-percent', `${minPercent}%`);
              range.style.setProperty('--price-max-percent', `${maxPercent}%`);
            };

            const syncPriceRange = (source) => {
              let minValue = clampValue(minInput.value, absoluteMin);
              let maxValue = clampValue(maxInput.value, absoluteMax);

              if (source === 'min-range') {
                minValue = Math.min(clampValue(minRange.value, absoluteMin), maxValue);
              } else if (source === 'max-range') {
                maxValue = Math.max(clampValue(maxRange.value, absoluteMax), minValue);
              } else {
                if (minValue > maxValue) {
                  if (source === 'min-input') {
                    maxValue = minValue;
                  } else {
                    minValue = maxValue;
                  }
                }
              }

              minInput.value = minValue;
              maxInput.value = maxValue;
              minRange.value = minValue;
              maxRange.value = maxValue;
              updateSliderTrack(minValue, maxValue);
            };

            syncPriceRange();

            minInput.addEventListener('input', () => syncPriceRange('min-input'));
            maxInput.addEventListener('input', () => syncPriceRange('max-input'));
            minRange.addEventListener('input', () => syncPriceRange('min-range'));
            maxRange.addEventListener('input', () => syncPriceRange('max-range'));
            minRange.addEventListener('change', applyPriceRange);
            maxRange.addEventListener('change', applyPriceRange);
          }

          range.addEventListener('focusout', (event) => {
            const nextFocused = event.relatedTarget;
            if (nextFocused && range.contains(nextFocused)) return;
            applyPriceRange();
          });

          range.querySelectorAll('input[type="number"]').forEach((field) => {
            field.addEventListener('keydown', (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (mediaQuery.matches) {
                  fetchPreviewCount();
                } else {
                  form.submit();
                }
              }
            });
          });
        });

        if (openButton) openButton.addEventListener('click', openDrawer);
        if (closeButton) closeButton.addEventListener('click', closeDrawer);
        if (overlay) overlay.addEventListener('click', closeDrawer);

        if (applyButton) {
          applyButton.addEventListener('click', () => {
            if (!mediaQuery.matches) return;
            applyButton.classList.add('is-loading');
          });
        }

        syncSortInputsState();
        syncCount(currentCount);

        window.addEventListener('resize', () => {
          syncSortInputsState();
          if (!mediaQuery.matches) closeDrawer();
        });
      });
    }
  };

  /**
   * Product card swatch hover preview
   */
  Comforty.ProductCardSwatches = {
    init() {
      document.querySelectorAll('[data-product-card]').forEach((card) => {
        if (card.dataset.enableSwatchImagePreview === 'false') return;

        const primaryImage = card.querySelector('[data-product-card-primary]');
        const hoverImage = card.querySelector('[data-product-card-hover]');
        const swatchButtons = Array.from(card.querySelectorAll('.product-card__swatch[data-swatch-image-src]'));

        if (!primaryImage || swatchButtons.length === 0) return;

        const defaultPrimary = {
          src: primaryImage.getAttribute('src') || '',
          srcset: primaryImage.getAttribute('srcset') || '',
          alt: primaryImage.getAttribute('alt') || ''
        };
        const defaultHover = hoverImage ? {
          src: hoverImage.getAttribute('src') || '',
          srcset: hoverImage.getAttribute('srcset') || '',
          alt: hoverImage.getAttribute('alt') || ''
        } : null;

        const applyImageState = (image, state) => {
          if (!image || !state) return;
          image.setAttribute('src', state.src || '');
          if (state.srcset) {
            image.setAttribute('srcset', state.srcset);
          } else {
            image.removeAttribute('srcset');
          }
          image.setAttribute('alt', state.alt || '');
        };

        const clearPreviewState = () => {
          swatchButtons.forEach((button) => button.classList.remove('is-preview-active'));
        };

        const showPreview = (button) => {
          const previewState = {
            src: button.dataset.swatchImageSrc || defaultPrimary.src,
            srcset: button.dataset.swatchImageSrcset || defaultPrimary.srcset,
            alt: button.dataset.swatchImageAlt || defaultPrimary.alt
          };

          clearPreviewState();
          button.classList.add('is-preview-active');
          applyImageState(primaryImage, previewState);
          if (hoverImage) applyImageState(hoverImage, previewState);
        };

        const restorePreview = () => {
          clearPreviewState();
          applyImageState(primaryImage, defaultPrimary);
          if (hoverImage && defaultHover) applyImageState(hoverImage, defaultHover);
        };

        swatchButtons.forEach((button) => {
          button.addEventListener('mouseenter', () => showPreview(button));
          button.addEventListener('focus', () => showPreview(button));
        });

        const swatchContainer = card.querySelector('.product-card__swatches');
        if (swatchContainer) {
          swatchContainer.addEventListener('mouseleave', restorePreview);
          swatchContainer.addEventListener('focusout', (event) => {
            const nextFocused = event.relatedTarget;
            if (nextFocused && swatchContainer.contains(nextFocused)) return;
            restorePreview();
          });
        }
      });
    }
  };

  /**
   * Hero section slider
   */
  Comforty.HeroSlider = {
    init() {
      document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
        const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
        const container = slider.closest('.hero');
        const prevBtn = container?.querySelector('[data-hero-prev]');
        const nextBtn = container?.querySelector('[data-hero-next]');
        const dots = Array.from(container?.querySelectorAll('[data-hero-dot]') || []);

        if (slides.length <= 1) return;

        let currentIndex = 0;
        let autoplayTimer = null;
        const autoplaySpeed = Number(container?.dataset.autoplaySpeed) || 5000;
        const isAutoplay = container?.dataset.autoplay === 'true';

        const goToSlide = (index) => {
          slides[currentIndex].classList.remove('is-active');
          if (dots[currentIndex]) dots[currentIndex].classList.remove('hero__dot--active');

          currentIndex = (index + slides.length) % slides.length;

          slides[currentIndex].classList.add('is-active');
          if (dots[currentIndex]) dots[currentIndex].classList.add('hero__dot--active');

          slider.style.transform = `translateX(-${currentIndex * 100}%)`;

          this.resetAutoplay();
        };

        const nextSlide = () => goToSlide(currentIndex + 1);
        const prevSlide = () => goToSlide(currentIndex - 1);

        this.startAutoplay = () => {
          if (!isAutoplay) return;
          this.stopAutoplay();
          autoplayTimer = setInterval(nextSlide, autoplaySpeed);
        };

        this.stopAutoplay = () => {
          if (autoplayTimer) clearInterval(autoplayTimer);
        };

        this.resetAutoplay = () => {
          this.stopAutoplay();
          this.startAutoplay();
        };

        if (prevBtn) prevBtn.addEventListener('click', prevSlide);
        if (nextBtn) nextBtn.addEventListener('click', nextSlide);

        dots.forEach((dot, index) => {
          dot.addEventListener('click', () => goToSlide(index));
        });

        // Pause on hover
        slider.addEventListener('mouseenter', () => this.stopAutoplay());
        slider.addEventListener('mouseleave', () => this.startAutoplay());

        this.startAutoplay();
      });
    }
  };

  /**
   * Initialize all modules
   */
  Comforty.init = function () {
    const modules = [
      ['Cart', this.Cart],
      ['Wishlist', this.Wishlist],
      ['MobileMenu', this.MobileMenu],
      ['DesktopMenu', this.DesktopMenu],
      ['ProductGallery', this.ProductGallery],
      ['ProductLightbox', this.ProductLightbox],
      ['ProductQuantity', this.ProductQuantity],
      ['ProductDescription', this.ProductDescription],
      ['ProductForm', this.ProductForm],
      ['ProductCardSwatches', this.ProductCardSwatches],
      ['CollectionFilters', this.CollectionFilters],
      ['ProductTabs', this.ProductTabs],
      ['Search', this.Search],
      ['LazyLoad', this.LazyLoad],
      ['Animations', this.Animations],
      ['HeroSlider', this.HeroSlider]
    ];

    modules.forEach(([name, module]) => {
      if (!module || typeof module.init !== 'function') return;

      try {
        module.init();
      } catch (error) {
        console.error(`Comforty.${name}.init failed:`, error);
      }
    });
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Comforty.init());
  } else {
    Comforty.init();
  }

})();
